import type { ChangeRequest, ValidationLane } from '@/domain/workflow.types'
import type { ChangeRequestDTO } from '@/services/work-order.service'

export const UNKNOWN_AUTHOR = 'Unknown user'

/**
 * Resolved once it has items and the reviewer has verified every one — the
 * worker's done ticks never resolve it on their own. The emptiness guard is
 * why this is one helper: `[].every(...)` is `true`.
 */
export function isChangeRequestResolved(changeRequest: Pick<ChangeRequest, 'items'>): boolean {
	return changeRequest.items.length > 0 && changeRequest.items.every((item) => item.verified)
}

export function laneChangeRequests(changeRequests: readonly ChangeRequest[], lane: ValidationLane): ChangeRequest[] {
	return changeRequests.filter((cr) => cr.lane === lane)
}

/** Any one unresolved request blocks the lane. */
export function laneHasOpenChangeRequest(changeRequests: readonly ChangeRequest[], lane: ValidationLane): boolean {
	return laneChangeRequests(changeRequests, lane).some((cr) => !isChangeRequestResolved(cr))
}

/**
 * Whether moving on from `statusCode` is held back by an open request sent
 * back there that still has an item not ticked done — mirrors the engine's
 * check on every forward move.
 */
export function hasUndoneChangeRequestItem(changeRequests: readonly ChangeRequest[], statusCode: string): boolean {
	return changeRequests.some(
		(cr) => cr.sentBackTo === statusCode && !isChangeRequestResolved(cr) && cr.items.some((item) => !item.checked),
	)
}

/** A request raised on a status with no validation lane has no lane-scoped surface, so it is dropped. */
export function toChangeRequests(
	dtos: readonly ChangeRequestDTO[],
	displayNameOf: (userId: string) => string | undefined,
): ChangeRequest[] {
	return dtos.flatMap((dto) =>
		dto.lane
			? [
					{
						changeRequestId: dto.instanceId,
						lane: dto.lane,
						authorId: dto.createdBy,
						authorName: (dto.createdBy && displayNameOf(dto.createdBy)) || UNKNOWN_AUTHOR,
						createdAt: dto.createdAt,
						sentBackTo: dto.sentBackToStatus,
						items: dto.items.map(({ itemId, label, required, checked, verified }) => ({
							itemId,
							label,
							required,
							checked,
							verified: verified === true,
						})),
					},
				]
			: [],
	)
}
